import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Megaphone, Plus, BarChart3, Eye, MousePointerClick,
  DollarSign, Loader2, RefreshCw, AlertCircle,
  Target, Search, Link2
} from 'lucide-react';
import AppLayout from '../../layouts/AppLayout';
import { DSButton, DSBadge, DSPageContainer } from '../../design-system/components';
import { metaAdsService, googleAdsService } from '../../services';
import './styles.css';

/* ── tiny helpers ─────────────────────── */
const fmt = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};
const money = (v) => (v == null ? '—' : `R$ ${(v / 100).toFixed(2)}`);

const statusColors = {
  ACTIVE: '#10b981',
  PAUSED: '#f59e0b',
  DELETED: '#ef4444',
  ARCHIVED: '#6b7280',
};

const PLATFORM_LABELS = { meta: '📘 Meta', google: '🔍 Google' };

function AdsManagerPage() {
  const navigate = useNavigate();
  const [metaStatus, setMetaStatus] = useState({ connected: false, loading: true });
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [campaigns, setCampaigns] = useState([]);     // unified list (meta + google)
  const [loading, setLoading] = useState(false);
  const [perfMap, setPerfMap] = useState({});
  const [loadingPerf, setLoadingPerf] = useState({});
  const [search, setSearch] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all'); // all | meta | google

  /* ── load connection statuses ──────── */
  useEffect(() => {
    (async () => {
      const [metaRes, googleRes] = await Promise.allSettled([
        metaAdsService.getStatus(),
        googleAdsService.getStatus(),
      ]);
      setMetaStatus({
        connected: !!metaRes.value?.connected,
        adAccountId: metaRes.value?.meta_ad_account_id,
        pageName: metaRes.value?.meta_page_name,
        loading: false,
      });
      setGoogleStatus({
        connected: !!googleRes.value?.connected,
        customerId: googleRes.value?.customer_id,
        loading: false,
      });
    })();
  }, []);

  /* ── load campaigns from both ──────── */
  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    const results = [];
    try {
      const [metaRes, googleRes] = await Promise.allSettled([
        metaStatus.connected ? metaAdsService.getCampaigns() : Promise.resolve(null),
        googleStatus.connected ? googleAdsService.listCampaigns() : Promise.resolve(null),
      ]);
      if (metaRes.value?.campaigns) {
        results.push(...metaRes.value.campaigns.map(c => ({ ...c, _platform: 'meta' })));
      }
      if (googleRes.value?.campaigns) {
        results.push(...googleRes.value.campaigns.map(c => ({ ...c, _platform: 'google' })));
      }
    } catch { /* noop */ }
    setCampaigns(results);
    setLoading(false);
  }, [metaStatus.connected, googleStatus.connected]);

  useEffect(() => {
    if (metaStatus.connected || googleStatus.connected) loadCampaigns();
  }, [metaStatus.connected, googleStatus.connected, loadCampaigns]);

  const anyConnected = metaStatus.connected || googleStatus.connected;

  /* ── load perf for one meta campaign ─ */
  const loadPerf = async (id, platform) => {
    if (perfMap[id] || loadingPerf[id] || platform !== 'meta') return;
    setLoadingPerf(p => ({ ...p, [id]: true }));
    try {
      const d = await metaAdsService.getPerformance(id);
      setPerfMap(p => ({ ...p, [id]: d }));
    } catch { /* noop */ }
    finally { setLoadingPerf(p => ({ ...p, [id]: false })); }
  };

  /* ── filtered campaigns ────────────── */
  const filtered = campaigns
    .filter(c => platformFilter === 'all' || c._platform === platformFilter)
    .filter(c => (c.name || '').toLowerCase().includes(search.toLowerCase()));

  /* ── aggregate metrics ─────────────── */
  const totals = campaigns.reduce((acc, c) => {
    const p = perfMap[c.id];
    if (p) {
      acc.impressions += p.impressions || 0;
      acc.clicks += p.clicks || 0;
      acc.spend += p.spend || 0;
    }
    return acc;
  }, { impressions: 0, clicks: 0, spend: 0 });

  /* ── render ────────────────────────── */
  return (
    <AppLayout>
      <div className="ads-mgr">
        {/* HEADER */}
        <header className="ads-mgr__header">
          <div className="ads-mgr__title-row">
            <div>
              <h1 className="ads-mgr__title"><Megaphone size={26} /> Gerenciador de Anúncios</h1>
              <p className="ads-mgr__subtitle">Gerencie campanhas no Meta Ads e Google Ads em um só lugar</p>
            </div>
            <div className="ads-mgr__header-actions">
              <DSButton variant="secondary" onClick={() => navigate('/ads/connect')}>
                <Link2 size={16} /> Conectar Contas
              </DSButton>
              <DSButton variant="primary" onClick={() => navigate('/ads/create')}>
                <Plus size={18} /> Nova Campanha
              </DSButton>
            </div>
          </div>
        </header>

        {/* Connection badges */}
        <div className="ads-mgr__conn-badges">
          <DSBadge color={metaStatus.connected ? 'green' : 'gray'} dot>
            📘 Meta {metaStatus.loading ? '...' : metaStatus.connected ? '✓' : '✗'}
          </DSBadge>
          <DSBadge color={googleStatus.connected ? 'green' : 'gray'} dot>
            🔍 Google {googleStatus.loading ? '...' : googleStatus.connected ? '✓' : '✗'}
          </DSBadge>
        </div>

        {/* NOT CONNECTED AT ALL */}
        {!metaStatus.loading && !googleStatus.loading && !anyConnected ? (
          <div className="ads-mgr__card ads-mgr__connect">
            <AlertCircle size={48} className="ads-mgr__connect-icon" />
            <h2>Conecte suas contas de anúncios</h2>
            <p>Para criar e gerenciar campanhas, conecte sua conta do Meta Ads e/ou Google Ads.</p>
            <DSButton variant="primary" onClick={() => navigate('/ads/connect')}>
              <Link2 size={18} /> Conectar Contas
            </DSButton>
          </div>
        ) : (metaStatus.loading && googleStatus.loading) ? (
          <div className="ads-mgr__loader"><Loader2 size={32} className="spin" /> Carregando...</div>
        ) : (
          <>
            {/* SUMMARY CARDS */}
            <div className="ads-mgr__summary">
              <SummaryCard icon={<Megaphone size={20} />} label="Campanhas" value={campaigns.length} color="#06B6D4" />
              <SummaryCard icon={<Eye size={20} />} label="Impressões" value={fmt(totals.impressions)} color="#7E22CE" />
              <SummaryCard icon={<MousePointerClick size={20} />} label="Cliques" value={fmt(totals.clicks)} color="#FFAD1D" />
              <SummaryCard icon={<DollarSign size={20} />} label="Gasto Total" value={money(totals.spend)} color="#10b981" />
            </div>

            {/* TOOLBAR */}
            <div className="ads-mgr__toolbar">
              <div className="ads-mgr__search">
                <Search size={16} />
                <input
                  placeholder="Buscar campanha..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="ads-mgr__filters">
                {['all', 'meta', 'google'].map(f => (
                  <button
                    key={f}
                    className={`ads-mgr__filter-btn ${platformFilter === f ? 'active' : ''}`}
                    onClick={() => setPlatformFilter(f)}
                  >
                    {f === 'all' ? 'Todas' : f === 'meta' ? '📘 Meta' : '🔍 Google'}
                  </button>
                ))}
              </div>
              <DSButton variant="ghost" size="sm" onClick={loadCampaigns} disabled={loading}>
                <RefreshCw size={16} className={loading ? 'spin' : ''} /> Atualizar
              </DSButton>
            </div>

            {/* TABLE */}
            {loading ? (
              <div className="ads-mgr__loader"><Loader2 size={28} className="spin" /> Carregando campanhas...</div>
            ) : filtered.length === 0 ? (
              <div className="ads-mgr__empty">
                <Target size={48} />
                <p>Nenhuma campanha encontrada.</p>
                <DSButton variant="primary" onClick={() => navigate('/ads/create')}>
                  <Plus size={16} /> Criar Primeira Campanha
                </DSButton>
              </div>
            ) : (
              <div className="ads-mgr__table-wrap">
                <table className="ads-mgr__table">
                  <thead>
                    <tr>
                      <th>Plataforma</th>
                      <th>Campanha</th>
                      <th>Status</th>
                      <th>Orçamento</th>
                      <th>Impressões</th>
                      <th>Cliques</th>
                      <th>CTR</th>
                      <th>Gasto</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(c => (
                      <CampaignRow key={`${c._platform}-${c.id}`} c={c} perf={perfMap[c.id]} loadPerf={loadPerf} loadingPerf={!!loadingPerf[c.id]} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}

/* ── Summary Card ─────────────────────── */
function SummaryCard({ icon, label, value, color }) {
  return (
    <div className="ads-mgr__sum-card" style={{ borderTopColor: color }}>
      <div className="ads-mgr__sum-icon" style={{ color }}>{icon}</div>
      <div className="ads-mgr__sum-value">{value}</div>
      <div className="ads-mgr__sum-label">{label}</div>
    </div>
  );
}

/* ── Campaign Row ─────────────────────── */
function CampaignRow({ c, perf, loadPerf, loadingPerf }) {
  useEffect(() => { loadPerf(c.id, c._platform); }, [c.id, c._platform, loadPerf]);

  const status = c.status || 'ACTIVE';
  const ctr = perf && perf.clicks && perf.impressions
    ? ((perf.clicks / perf.impressions) * 100).toFixed(2) + '%'
    : '—';

  return (
    <tr className="ads-mgr__row">
      <td>
        <span className={`ads-mgr__platform-tag ${c._platform}`}>
          {PLATFORM_LABELS[c._platform] || c._platform}
        </span>
      </td>
      <td className="ads-mgr__cell-name">
        <span className="ads-mgr__camp-name">{c.name || `Campanha #${c.id}`}</span>
        {c.objective && <span className="ads-mgr__camp-obj">{c.objective}</span>}
      </td>
      <td>
        <span className="ads-mgr__status" style={{ background: statusColors[status] + '22', color: statusColors[status] }}>
          {status}
        </span>
      </td>
      <td>{c.daily_budget ? money(c.daily_budget) + '/dia' : '—'}</td>
      <td>{loadingPerf ? <Loader2 size={14} className="spin" /> : fmt(perf?.impressions)}</td>
      <td>{loadingPerf ? <Loader2 size={14} className="spin" /> : fmt(perf?.clicks)}</td>
      <td>{loadingPerf ? <Loader2 size={14} className="spin" /> : ctr}</td>
      <td>{loadingPerf ? <Loader2 size={14} className="spin" /> : money(perf?.spend)}</td>
      <td>
        <button className="ads-mgr__row-btn" title="Ver Performance">
          <BarChart3 size={16} />
        </button>
      </td>
    </tr>
  );
}

export default AdsManagerPage;
